import { z } from 'zod';
import { PERMISSIONS, ROLES } from '../roles.js';

export const loginSchema = z.object({
  email: z.string().trim().email().max(200),
  password: z.string().min(1).max(200),
  /** Sechsstelliger Code aus der Authenticator-App, sofern eingerichtet. */
  totp: z
    .string()
    .trim()
    .regex(/^\d{6}$/)
    .optional(),
});

export const sessionUserSchema = z.object({
  id: z.number().int(),
  email: z.string(),
  name: z.string(),
  role: z.enum(ROLES),
  totp_enrolled: z.boolean(),
  permissions: z.array(z.string()),
  /** True, solange der zweite Faktor für die Admin-Rolle noch fehlt. */
  setup_required: z.boolean().optional(),
});
export type SessionUser = z.infer<typeof sessionUserSchema>;

/**
 * Antwort auf die Passwortanmeldung. Für die Admin-Rolle ist ein zweiter
 * Faktor Pflicht — die Antwort sagt, welcher Schritt noch fehlt.
 */
export const loginResponseSchema = z.discriminatedUnion('status', [
  z.object({ status: z.literal('ok'), user: sessionUserSchema }),
  /** Passwort stimmt, jetzt fehlt noch der Einmalcode aus der Authenticator-App. */
  z.object({ status: z.literal('totp_required') }),
  /**
   * Admin mit Passkey, aber ohne Authenticator: Der Passkey ist der zweite
   * Faktor und muss benutzt werden — das Passwort allein öffnet nicht.
   */
  z.object({ status: z.literal('passkey_required') }),
  /**
   * Admin ohne jeden zweiten Faktor: Es entsteht eine kurzlebige
   * Einrichtungssitzung, mit der sich Passkey oder Authenticator hinterlegen lässt.
   */
  z.object({
    status: z.literal('setup_required'),
    setup: z.object({ secret: z.string(), otpauth_url: z.string() }),
  }),
]);
export type LoginResponse = z.infer<typeof loginResponseSchema>;

/**
 * Passkeys (WebAuthn) — gleichwertige Anmeldemethode neben Passwort.
 * Da ein Passkey gerätegebunden und mit Biometrie oder PIN entsperrt ist,
 * gilt er selbst als zweiter Faktor; die TOTP-Abfrage entfällt dann.
 */
export const passkeySchema = z.object({
  id: z.number().int(),
  name: z.string().nullable(),
  device_type: z.string().nullable(),
  backed_up: z.boolean(),
  last_used_at: z.string().nullable(),
  created_at: z.string(),
});

/** Anmeldung ohne Passwort: leer lassen für Passkeys mit Kontoauswahl im Browser. */
export const passkeyLoginOptionsSchema = z.object({
  email: z.string().trim().email().max(200).optional(),
});

/** Rohantwort des Browsers — Aufbau gibt der WebAuthn-Standard vor. */
export const passkeyVerifySchema = z.object({
  response: z.unknown(),
});

export const passkeyRegisterSchema = z.object({
  response: z.unknown(),
  name: z.string().trim().max(120).optional(),
});

export const passkeyRenameSchema = z.object({
  name: z.string().trim().min(1).max(120),
});

/**
 * Bestätigt die Einrichtung des Authenticators. Wer das ist, sagt die
 * Sitzung — nicht der Rumpf. Das Geheimnis wird trotzdem mitgeschickt, damit
 * genau der Schlüssel bestätigt wird, der gerade angezeigt wurde.
 */
export const totpSetupConfirmSchema = z.object({
  secret: z.string().min(16).max(128),
  code: z.string().regex(/^\d{6}$/),
});

export const inviteAcceptSchema = z.object({
  token: z.string().min(20).max(200),
  password: z
    .string()
    .min(12, 'Mindestens 12 Zeichen')
    .max(200)
    .regex(/[a-zäöüß]/, 'Mindestens ein Kleinbuchstabe')
    .regex(/[A-ZÄÖÜ]/, 'Mindestens ein Großbuchstabe')
    .regex(/\d/, 'Mindestens eine Ziffer'),
});

export const userSchema = z.object({
  id: z.number().int(),
  email: z.string(),
  name: z.string(),
  role: z.enum(ROLES),
  is_active: z.boolean(),
  totp_enrolled: z.boolean(),
  /** Zahl der hinterlegten Passkeys — ab eins gilt der zweite Faktor als vorhanden. */
  passkeys: z.number().int(),
  /** Eingeladen, aber noch nie ein Passwort vergeben. */
  invite_pending: z.boolean(),
  /** Ablauf des offenen Einladungslinks — null, wenn keiner offen ist. */
  invite_expires_at: z.string().nullable(),
  last_login_at: z.string().nullable(),
  created_at: z.string(),
});

/** Antwort auf eine (erneut) verschickte Einladung. */
export const userInviteResultSchema = z.object({
  invite_link: z.string(),
  invite_expires_at: z.string(),
  /** False, wenn der Mailversand scheiterte — der Link gilt trotzdem. */
  mail_sent: z.boolean(),
});
export type UserInviteResult = z.infer<typeof userInviteResultSchema>;

export const userCreateSchema = z.object({
  email: z.string().trim().email().max(200),
  name: z.string().trim().min(1).max(160),
  role: z.enum(ROLES),
  /** Verknüpfung mit dem Mitarbeiterstammsatz, falls die Person Geräte trägt. */
  employee_id: z.number().int().positive().nullable().optional(),
  send_invite: z.boolean().default(true),
});

export const userUpdateSchema = z.object({
  name: z.string().trim().min(1).max(160).optional(),
  role: z.enum(ROLES).optional(),
  is_active: z.boolean().optional(),
  /** Authenticator entfernen — die Person richtet ihn beim nächsten Anmelden neu ein. */
  reset_totp: z.boolean().optional(),
  /**
   * Alle Passkeys entfernen. Der Rettungsweg, wenn das Gerät mit dem einzigen
   * Passkey verloren ging — sonst käme ein Admin nicht mehr hinein.
   */
  reset_passkeys: z.boolean().optional(),
});

export const apiTokenCreateSchema = z.object({
  name: z.string().trim().min(1).max(120),
  role: z.enum(ROLES),
  /** Engt die Rolle auf einzelne Rechte ein; leer oder `*` heißt: alles der Rolle. */
  scopes: z.array(z.enum([...PERMISSIONS, '*'])).default([]),
  expires_at: z.string().datetime().nullable().optional(),
});

export const apiTokenSchema = z.object({
  id: z.number().int(),
  name: z.string(),
  role: z.enum(ROLES),
  scopes: z.array(z.string()),
  last_used_at: z.string().nullable(),
  expires_at: z.string().nullable(),
  revoked_at: z.string().nullable(),
  created_at: z.string(),
});
